import React, { useState, useRef, useEffect } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useToggle } from "../../../hooks";
import { destroy, post, put, get } from "../../../utils/DeApi";
import ErrorHandler from "../../ErrorHandler/ErrorHandler";

const CikModal = ({
  mode = "Add",
  cikObject = {},
  onClose = () => null,
  show = false,
  userAction = () => null,
  setAlertShow = () => false,
}) => {
  const [bdcName, setBdcName] = useState(cikObject?.name || "");
  const [cik, setCik] = useState(cikObject?.cik || "");
  const [ticker, setTicker] = useState(cikObject?.ticker || "");
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  const [error, setError] = useState(null);
  const subscribedPromises = useRef([]);
  const [ciks, setCiks] = useState([]);

  useEffect(() => {
    if (cikObject && Object.hasOwn(cikObject, "name")) {
      setBdcName(cikObject?.name || null);
      setCik(cikObject?.cik || null);
      setTicker(cikObject?.ticker || null);
    }
  }, [cikObject]);

  useEffect(() => {
    const promises = subscribedPromises.current;
    return () => {
      promises.forEach((cancel) => cancel());
    };
  }, []);

  useEffect(() => {
    if (ciks && ciks.length <= 0) {
      const { promise, cancel } = get("ciks");
      promise
        .then((data) => {
          let records = data.data;
          if (records && records.length === 0) {
            return setCiks([]);
          }
          setCiks(
            records.sort((a, b) => {
              if (a.name.toLowerCase() < b.name.toLowerCase()) {
                return -1;
              }
              if (a.name.toLowerCase() > b.name.toLowerCase()) {
                return 1;
              }
              return 0;
            })
          );
        })
        .catch((error) => setError(error));

      subscribedPromises.current.push(
        cancel
      ); /* *** updating array with cancelable of promise *** */
    }
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    console.log("mode: ", mode);
    const isAlreadyExists = ciks.find(
      (cikItem) => cikItem.cik == cik || cikItem.ticker === ticker.trim()
    );
    if (isAlreadyExists && mode !== "Update") {
      const error = {
        status: "",
        statusText: "",
        data: {
          error: {
            message: "Cik or Ticker already exists",
          },
        },
      };
      setError(error);
      return;
    }

    toggleIsSubmitting();

    const { promise, cancel } =
      mode === "Add"
        ? post("cik", { bdcName, cik, ticker })
        : put(`cik/${cikObject?.id}`, { bdcName, ticker, cik });
    /* *** subscribing promise *** */
    promise
      .then((response) => {
        setCik("");
        setTicker("");
        setBdcName("");
        if (mode === "Add") {
          userAction(response);
        } else {
          userAction([]);
        }

        setCiks((prevState) => [...prevState, response.data]);
        setAlertShow(true);
        onClose(response.data, mode);
      })
      .catch((error) => setError(error))
      .finally(toggleIsSubmitting);
    /* *** keeping used promise instance *** */
    subscribedPromises.current.push(cancel);
  };

  if (mode === "Delete") {
    return (
      <DeleteCikTypeContent show={show} cik={cikObject} onClose={onClose} />
    );
  }

  return (
    <Modal
      show={show}
      onHide={() => {
        onClose();
        setError(null);
      }}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className="color-deal-style">
          {mode} Keyword Type
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Error Handling ( Below ) */}
        {error && <ErrorHandler error={error} />}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label>BDC Name</Form.Label>
            <Form.Control
              type="text"
              className="roundedEdges"
              required
              value={bdcName}
              placeholder="Enter BDC Name"
              onChange={(e) => setBdcName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label>Ticker</Form.Label>
            <Form.Control
              type="text"
              className="roundedEdges"
              required
              value={ticker}
              placeholder="Enter Ticker"
              onChange={(e) => setTicker(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label>Cik#</Form.Label>
            <Form.Control
              type="number"
              className="roundedEdges"
              required
              value={cik}
              placeholder="Enter Cik"
              onChange={(e) => setCik(e.target.value)}
            />
          </Form.Group>
          <Button
            type="submit"
            className="float-right btn btn-success-deal-style roundedEdges"
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <Spinner
                as="span"
                animation="border"
                className="mb-1"
                variant="light"
                size="sm"
              />
            )}{" "}
            {mode}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

const DeleteCikTypeContent = ({ cik, onClose, show }) => {
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  const [error, setError] = useState(null);
  const subscribedPromises = useRef([]);

  const handleDelet = () => {
    toggleIsSubmitting();
    const { promise, cancel } = destroy(`cik/${cik.id}`);
    promise
      .then((response) => onClose(cik))
      .catch((error) => setError(error))
      .finally(toggleIsSubmitting);
    subscribedPromises.current.push(cancel);
  };

  useEffect(() => {
    const promises = subscribedPromises.current;
    return () => promises.forEach((cancel) => cancel());
  }, []);

  /* *** returning content ( below ) *** */
  return (
    <Modal
      show={show}
      onHide={() => {
        onClose();
        setError();
      }}
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title className="color-deal-style">
          Delete Keyword Type
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Are you sure you want to delete{" "}
        <span className="font-font-weight-bold text-danger">{cik?.name}</span>?
        {error && <ErrorHandler error={error} />}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="danger"
          onClick={handleDelet}
          className="roundedEdges"
          disabled={isSubmitting}
        >
          {isSubmitting && (
            <Spinner
              as="span"
              animation="border"
              variant="light"
              size="sm"
              className="m-1"
            />
          )}{" "}
          Yes, Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default CikModal;
